// @flow
import React, { Component, Fragment } from 'react'
import DropZone from './DropZone'
import Preview from './Preview'
import TextPreview from './TextPreview'
import styled, { type StyledComponent } from 'styled-components'
import _ from 'lodash'
import type { Media, Video, Image } from '../../types'
import Tooltip from '../../Tooltip'
import Q from 'q'

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  vertical-align: top;
`
const Note: StyledComponent<Object, any, any> = styled.div`
  color: black;
  font-size: 12px;
`

export type SelectResult = {|
  success: Array<{| media: Media, file: File |}>,
  failed: Array<{| errors: Array<string>, file: File |}>
|}

type Props = {|
  media: ?Media,
  isLoading: boolean,
  height: ?number,
  width: ?number,
  errors: Array<string>,
  notes: Array<string>,
  onSelect: (SelectResult) => void,
  minWidth?: number,
  minHeight?: number,
  maxWidth?: number,
  maxHeight?: number,
  withoutMediaPreview?: boolean,
  title?: string,
  titleFontSize?: number,
  mediaType: 'image' | 'video',
  skipImageValidation?: boolean,
  multiple?: boolean,
  maxImageSize: number
|}

export default class MediaPicker extends Component<Props> {
  dropzoneRef: any

  constructor(props: Props) {
    super(props)

    this.dropzoneRef = React.createRef()
  }

  render() {
    const {
      media,
      isLoading,
      height,
      width,
      errors,
      notes,
      withoutMediaPreview,
      title,
      titleFontSize,
      mediaType,
      multiple
    } = this.props
    const triggered = () => this.dropzoneRef.current.open()
    const previewMedia: any = media

    return (
      <Wrapper>
        <DropZone
          ref={this.dropzoneRef}
          acceptType={mediaType + '/*'}
          multiple={multiple}
          onSelect={(files) => this.mediaSelected(files)}
        >
          <Tooltip content={this.tooltipContent()} isDisabled={!media}>
            {withoutMediaPreview ? (
              <TextPreview
                media={previewMedia}
                isLoading={isLoading}
                errors={errors}
                title={title}
                titleFontSize={titleFontSize}
                onOpenFileDialog={triggered}
              />
            ) : (
              <Preview
                media={previewMedia}
                isLoading={isLoading}
                height={height}
                width={width}
                errors={errors}
                notes={notes}
                onOpenFileDialog={triggered}
              />
            )}
          </Tooltip>
        </DropZone>
      </Wrapper>
    )
  }

  tooltipContent() {
    const { media } = this.props

    if (!media) return null
    return (
      <Fragment>
        <Note>{media.name}</Note>
        <Note>
          {media.width}px x {media.height}px
        </Note>
      </Fragment>
    )
  }

  mediaSelected(files: Array<File>) {
    const { mediaType } = this.props
    let promises = []
    if (mediaType === 'image') {
      promises = _.map(files, (file) => {
        return this.imageSelected(file)
      })
    } else if (mediaType === 'video') {
      promises = _.map(files, (file) => {
        return this.videoSelected(file)
      })
    } else {
      throw new Error(`Unknown mediaType: ${mediaType}`)
    }
    Q.allSettled(promises).then((results) => {
      this.props.onSelect({
        success: _.map(
          _.filter(results, (res) => res.state === 'fulfilled'),
          (res) => res.value
        ),
        failed: _.map(
          _.filter(results, (res) => res.state === 'rejected'),
          (res) => res.reason
        )
      })
    })
  }

  imageSelected(file: any): Promise<Function> {
    return new Promise((resolve, reject) => {
      const reader = new window.FileReader()
      const { name, size } = file
      const {
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        skipImageValidation,
        maxImageSize
      } = this.props

      let errors = []
      const mb = Math.pow(2, 20)

      if (!skipImageValidation && size > maxImageSize * mb) {
        errors.push(`размер ${Math.round((size / mb) * 10) / 10}M > ${maxImageSize}M`)
      }

      reader.onload = (e) => {
        const src = e.target.result

        const img = new window.Image()

        img.onload = () => {
          const { width, height } = img

          const imageData: Image = {
            name,
            size,
            width,
            height,
            src: src
          }

          if (!skipImageValidation) {
            if (maxWidth && width > maxWidth) {
              errors.push(`ширина ${width}px > ${maxWidth}px`)
            }
            if (maxHeight && height > maxHeight) {
              errors.push(`высота ${height}px > ${maxHeight}px`)
            }
            if (minWidth && width < minWidth) {
              errors.push(`ширина ${width}px < ${minWidth}px`)
            }
            if (minHeight && height < minHeight) {
              errors.push(`высота ${height}px < ${minHeight}px`)
            }
          }

          if (_.isEmpty(errors)) {
            resolve({ media: imageData, file })
          } else {
            reject({ errors, file }) // eslint-disable-line prefer-promise-reject-errors
          }
        }

        img.src = src
      }

      reader.readAsDataURL(file)
    })
  }

  videoSelected(file: any): Promise<Function> {
    return new Promise((resolve, reject) => {
      const reader = new window.FileReader()
      const { name, size } = file

      let errors = []
      const mb = Math.pow(2, 20)

      if (size > 10.0 * mb) {
        errors.push(`размер ${Math.round((size / mb) * 10) / 10}M > 10M`)
      }

      reader.onload = (e) => {
        const src = e.target.result

        const vid = document.createElement('video')
        vid.src = src

        const { width, height } = vid

        const videoData: Video = {
          name,
          size,
          width,
          height,
          src
        }
        if (_.isEmpty(errors)) {
          resolve({ media: videoData, file })
        } else {
          reject({ errors, file }) // eslint-disable-line prefer-promise-reject-errors
        }
      }

      reader.readAsDataURL(file)
    })
  }
}
